import React, { useState } from 'react';

import { Box, BoxProps, Button, Card, Flex, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { IconPlus } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { ImportPhoneNumberModal } from 'components/SmartAssistantSections/TelephonySection/ImportPhoneNumberModal';
import { PhoneNumberTable } from 'components/SmartAssistantSections/TelephonySection/PhoneNumberTable';
import { PurchasePhoneNumberModal } from 'components/SmartAssistantSections/TelephonySection/PurchasePhoneNumberModal/PurchasePhoneNumberModal';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { TelephonyService } from 'services/telephony-service';
import { PHONE_NUMBERS_QUERY_KEY } from 'shared/constants/query-keys';
import { TelephonyFormValues } from 'shared/interfaces/SmartAssistant';
import { PhoneNumber, PurchasePhoneNumberDto } from 'shared/interfaces/Telephony';

interface TelephonySectionProps extends BoxProps {
  form?: UseFormReturnType<TelephonyFormValues>;
  sortPhoneNumbers?: boolean;
}

export function TelephonySection({ form, sortPhoneNumbers, ...boxProps }: TelephonySectionProps) {
  const { t } = useTranslation();
  const [importPhoneNumbersModalOpen, setImportPhoneNumbersModalOpen] = useState(false);
  const [purchasePhoneNumberModalOpen, setPurchasePhoneNumberModalOpen] = useState(false);

  const phoneNumbersQuery = useQuery({
    queryKey: [PHONE_NUMBERS_QUERY_KEY],
    queryFn: TelephonyService.getNumbers
  });

  const purchasePhoneNumberMutation = useMutation({
    mutationFn: TelephonyService.purchasePhoneNumber,
    onSuccess: () => {
      phoneNumbersQuery.refetch();
      setPurchasePhoneNumberModalOpen(false);
      NotificationService.showResult(true, 'FEED_BACK.TELEPHONY.SUCCESS.PURCHASE');
    },
    onError: (error: AxiosError) => {
      if (error.response?.status === HttpStatusCode.PaymentRequired) {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.PURCHASE_MODAL.USAGE_LIMIT_ERROR_MESSAGE', error);
      } else if (error.response?.status === HttpStatusCode.Conflict) {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.PURCHASE_MODAL.CONFLICT_ERROR_MESSAGE', error);
      } else {
        NotificationService.showResult(false, 'FEED_BACK.TELEPHONY.FAILURE.PURCHASE');
      }
    }
  });

  const handleImportNumberSuccess = () => {
    phoneNumbersQuery.refetch();
    setImportPhoneNumbersModalOpen(false);
  };

  const handlePurchaseNumber = (purchasePhoneNumberDto: PurchasePhoneNumberDto) => {
    purchasePhoneNumberMutation.mutate(purchasePhoneNumberDto);
  };

  const handleRowClick = (phoneNumber: PhoneNumber) => {
    if (!form) return;
    if (phoneNumber._id === form.values.telephonyConfiguration?.phoneNumber) {
      form.setFieldValue('telephonyConfiguration', undefined);
    } else {
      form.setFieldValue('telephonyConfiguration', { phoneNumber: phoneNumber._id });
    }
  };

  // Make sure phone numbers without smart assistants are at the top, then sort alphabetically by phone number.
  const phoneNumbers = sortPhoneNumbers
    ? (phoneNumbersQuery.data ?? []).sort((a, b) => {
        const aSmartAssistantId = a.deploymentConfiguration?.smartAssistantId;
        const bSmartAssistantId = b.deploymentConfiguration?.smartAssistantId;

        if (aSmartAssistantId && !bSmartAssistantId) return 1;
        if (!aSmartAssistantId && bSmartAssistantId) return -1;
        return a.twilioPhoneNumberConfiguration.phoneNumber.localeCompare(b.twilioPhoneNumberConfiguration.phoneNumber);
      })
    : phoneNumbersQuery.data ?? [];

  const selectedPhoneNumber = form?.values.telephonyConfiguration?.phoneNumber;

  return (
    <Box {...boxProps}>
      <Card withBorder>
        <Card.Section p="md">
          <PhoneNumberTable
            isLoading={phoneNumbersQuery.isLoading}
            phoneNumbers={phoneNumbers}
            selectedNumber={selectedPhoneNumber}
            onRowClick={handleRowClick}
            disableSelect={!form}
            enableDelete={!form}
          />
        </Card.Section>

        <Card.Section withBorder inheritPadding py="md">
          <Flex justify="space-between" align="center">
            <Box>
              {selectedPhoneNumber && (
                <Text size="sm" color="dimmed">
                  {`${t('SMART_ASSISTANT.TELEPHONY_SECTION.SELECTED_NUMBER')} `}
                  <Text component="span" weight={500}>
                    {
                      phoneNumbers.find((number) => number._id === selectedPhoneNumber)?.twilioPhoneNumberConfiguration
                        .friendlyPhoneNumber
                    }
                  </Text>
                </Text>
              )}
            </Box>
            <Flex gap="sm">
              <Button variant="outline" onClick={() => setImportPhoneNumbersModalOpen(true)}>
                {t('SMART_ASSISTANT.TELEPHONY_SECTION.BUTTONS.IMPORT_FROM_TWILIO')}
              </Button>
              <Button leftIcon={<IconPlus size={14} />} onClick={() => setPurchasePhoneNumberModalOpen(true)}>
                {t('SMART_ASSISTANT.TELEPHONY_SECTION.BUTTONS.PURCHASE_NEW_NUMBER')}
              </Button>
            </Flex>
          </Flex>
        </Card.Section>
      </Card>

      <ImportPhoneNumberModal
        opened={importPhoneNumbersModalOpen}
        onClose={() => setImportPhoneNumbersModalOpen(false)}
        onImportSuccess={handleImportNumberSuccess}
      />

      <PurchasePhoneNumberModal
        opened={purchasePhoneNumberModalOpen}
        onClose={() => setPurchasePhoneNumberModalOpen(false)}
        onPurchaseConfirm={handlePurchaseNumber}
      />
    </Box>
  );
}
